import React, { useState, useEffect, useCallback } from 'react';
import { UserFiltersState, RegistrationMethod, AccountStatus } from '../types';
import './UserFilters.css';

interface UserFiltersProps {
  filters: UserFiltersState;
  onFiltersChange: (filters: UserFiltersState) => void;
  totalCount: number;
  filteredCount: number;
}

export const UserFilters: React.FC<UserFiltersProps> = ({
  filters,
  onFiltersChange,
  totalCount,
  filteredCount,
}) => {
  const [searchInput, setSearchInput] = useState(filters.search);

  // Debounce search input by 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        onFiltersChange({ ...filters, search: searchInput });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, filters, onFiltersChange]);

  const handleAuthMethodChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    onFiltersChange({ ...filters, authMethod: e.target.value as RegistrationMethod | 'all' });
  }, [filters, onFiltersChange]);

  const handleStatusChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    onFiltersChange({ ...filters, status: e.target.value as AccountStatus | 'all' });
  }, [filters, onFiltersChange]);

  const handlePageSizeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    onFiltersChange({ ...filters, pageSize: parseInt(e.target.value) as 10 | 25 | 50 | 100 });
  }, [filters, onFiltersChange]);

  const handleSortChange = useCallback((sortBy: UserFiltersState['sortBy']) => {
    const newOrder = filters.sortBy === sortBy && filters.sortOrder === 'desc' ? 'asc' : 'desc';
    onFiltersChange({ ...filters, sortBy, sortOrder: newOrder });
  }, [filters, onFiltersChange]);

  const isFiltered = filters.search || filters.authMethod !== 'all' || filters.status !== 'all';

  return (
    <div className="user-filters">
      <div className="user-filters__row">
        <div className="user-filters__search">
          <input
            type="text"
            className="user-filters__search-input"
            placeholder="Search by name or email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search users"
          />
        </div>

        <div className="user-filters__dropdowns">
          <select
            className="user-filters__select"
            value={filters.authMethod}
            onChange={handleAuthMethodChange}
            aria-label="Filter by registration method"
          >
            <option value="all">All Methods</option>
            <option value="google">Google</option>
            <option value="facebook">Facebook</option>
            <option value="yahoo">Yahoo</option>
            <option value="email">Email</option>
            <option value="guest">Guest</option>
          </select>

          <select
            className="user-filters__select"
            value={filters.status}
            onChange={handleStatusChange}
            aria-label="Filter by account status"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="banned">Banned</option>
          </select>

          <select
            className="user-filters__select"
            value={filters.pageSize}
            onChange={handlePageSizeChange}
            aria-label="Page size"
          >
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>
      </div>

      <div className="user-filters__info-row">
        <div className="user-filters__count">
          {isFiltered ? (
            <span>Showing {filteredCount} of {totalCount} users</span>
          ) : (
            <span>{totalCount} users total</span>
          )}
        </div>

        <div className="user-filters__sort-controls">
          <span className="user-filters__sort-label">Sort by:</span>
          {(['createdAt', 'lastActiveAt', 'gamesPlayed', 'winRate'] as const).map((col) => (
            <button
              key={col}
              className={`user-filters__sort-btn ${filters.sortBy === col ? 'user-filters__sort-btn--active' : ''}`}
              onClick={() => handleSortChange(col)}
              aria-label={`Sort by ${col}`}
            >
              {col === 'createdAt' ? 'Registered' :
               col === 'lastActiveAt' ? 'Last Active' :
               col === 'gamesPlayed' ? 'Games' : 'Win Rate'}
              {filters.sortBy === col && (
                <span className="user-filters__sort-arrow">
                  {filters.sortOrder === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
